<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import { ref, onMounted } from 'vue'
import { useStoreLogger } from '@/logger.ts'
import { Button as KButton } from '@progress/kendo-vue-buttons'

const logger = useStoreLogger()

const props = defineProps<{
  masterOrderId: string
  trackingUrl?: string
}>()

interface ITrackingData {
  status: string
  estimatedDelivery: Date
  trackingNumber: string
  carrier: string
}

const trackingData = ref<ITrackingData | null>(null)
const isLoading = ref<boolean>(true)
const error = ref<string>('')

onMounted(async () => {
  logger.debug('AoeParcelLab onMounted', props.masterOrderId)
  await loadTrackingData()
})

async function loadTrackingData() {
  try {
    isLoading.value = true
    
    trackingData.value = {
      status: 'In Transit',
      estimatedDelivery: new Date(),
      trackingNumber: 'PL123456789',
      carrier: 'Parcel Lab'
    }
  } catch (err) {
    logger.error('Failed to load tracking data', err)
    error.value = t('parcelLab.LoadError')
  } finally {
    isLoading.value = false
  }
}

function generateParcelLabUrl(): string {
  return `/POE/ParcelLabTrackingModule?MasterOrderId=${props.masterOrderId}`
}

function openFullTracking() {
  const url = generateParcelLabUrl()
  window.open(url, '_blank')
}
</script>

<template>
  <div class="aoe-parcel-lab">
    <div class="row">
      <div class="col-12 parcel-lab-header">
        <h4>{{ t('parcelLab.TrackingTitle') }}</h4>
      </div>
    </div>
    
    <div v-if="isLoading" class="row">
      <div class="col-12 text-center">
        <div class="spinner-border" role="status">
          <span class="sr-only">{{ t('parcelLab.Loading') }}</span>
        </div>
      </div>
    </div>
    
    <div v-else-if="error" class="row">
      <div class="col-12 alert alert-danger">
        {{ error }}
      </div>
    </div>
    
    <div v-else class="row">
      <div class="col-12">
        <div class="parcel-lab-container">
          <iframe 
            :src="generateParcelLabUrl()"
            class="parcel-lab-iframe"
            frameborder="0"
            width="100%"
            height="600">
          </iframe>
        </div>
        
        <div class="row mt-3">
          <div class="col-12 text-center">
            <KButton
              @click="openFullTracking"
              class="baseBtn primaryBtn"
              :rounded="'large'">
              {{ t('parcelLab.ViewFullTracking') }}
            </KButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.aoe-parcel-lab {
  padding: $card-padding;
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  background-color: var(--vt-c-white);
}

.parcel-lab-header h4 {
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 20px;
}

.parcel-lab-container {
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  overflow: hidden;
}

.parcel-lab-iframe {
  min-height: 600px;
}

@media (max-width: 768px) {
  .aoe-parcel-lab {
    padding: $spacing-4;
  }
  
  .parcel-lab-iframe {
    min-height: 500px;
  }
}
</style>
